import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, BadgeCheck, BookOpenCheck } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import usamaPhoto from "@/assets/usama-ali.webp";
import { SITE_URL } from "@/lib/seo-constants";

const reviews = [
  { to: "/guides/what-is-the-mini-pill/", label: "What is the mini pill?" },
  { to: "/guides/what-is-the-combined-pill/", label: "What is the combined pill?" },
  { to: "/guides/cerelle/", label: "Cerelle" },
  { to: "/guides/levest/", label: "Levest" },
  { to: "/guides/side-effects-of-the-pill/", label: "Side effects of the pill" },
  { to: "/guides/is-contraception-direct-safe/", label: "Is Contraception Direct safe and legit?" },
];

const AuthorUsamaAli = () => (
  <>
    <Helmet>
      <title>Usama Ali, MPharm (GPhC 2224726) | Contraception Direct</title>
      <meta
        name="description"
        content="Usama Ali is a GPhC-registered pharmacist (2224726) and co-founder of Contraception Direct. He reviews the clinical content on this site against NHS and FSRH guidance."
      />
      <link rel="canonical" href={`${SITE_URL}/authors/usama-ali/`} />
      <meta property="og:title" content="Usama Ali, MPharm (GPhC 2224726)" />
      <meta property="og:description" content="GPhC-registered pharmacist and clinical content reviewer at Contraception Direct." />
      <meta property="og:url" content={`${SITE_URL}/authors/usama-ali/`} />
      <meta property="og:type" content="profile" />
      <meta property="og:image" content={`${SITE_URL}/og/guides/usama-ali.png`} />
    </Helmet>

    <Navbar />

    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <nav className="text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-primary">Home</Link>
        <span className="mx-1.5">&rsaquo;</span>
        <span className="text-foreground/80">Usama Ali</span>
      </nav>

      <header className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6 mb-8">
        <img
          src={usamaPhoto}
          alt="Usama Ali, MPharm, pharmacist at Contraception Direct"
          className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover object-top ring-1 ring-primary/20 shrink-0"
          width={112}
          height={112}
        />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Usama Ali, MPharm</h1>
          <p className="text-foreground/70 mt-1">Co-founder and Pharmacist · Clinical content reviewer</p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-sm">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <a
              href="https://www.pharmacyregulation.org/registers/pharmacist/2224726"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              GPhC registration 2224726
            </a>
          </p>
        </div>
      </header>

      <div className="prose prose-slate max-w-none">
        <p>
          Usama Ali is a pharmacist registered with the General Pharmaceutical Council (GPhC 2224726) and a co-founder of Contraception Direct. He is responsible for reviewing the clinical content on this site so that what you read is accurate, up to date, and in line with national guidance.
        </p>

        <h2 className="flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-primary" /> Credentials and registration</h2>
        <ul>
          <li>Master of Pharmacy (MPharm).</li>
          <li>Registered pharmacist with the General Pharmaceutical Council, registration number 2224726. You can verify this on the <a href="https://www.pharmacyregulation.org/registers/pharmacist/2224726" target="_blank" rel="noopener noreferrer">GPhC register</a>.</li>
          <li>Co-founder of Contraception Direct, the online contraception service delivered by a GPhC-registered pharmacy (registration 9013012) through the NHS Pharmacy Contraception Service.</li>
        </ul>

        <h2 className="flex items-center gap-2"><BookOpenCheck className="w-5 h-5 text-primary" /> What Usama reviews, and how</h2>
        <p>
          Every guide on Contraception Direct is checked against current NHS guidance, the Faculty of Sexual and Reproductive Healthcare (FSRH), the British National Formulary (BNF), and the Summary of Product Characteristics (SPC) for each medicine. Where a guide names a specific pill, the information is used for recognition and education, not promotion, and always pairs benefits with risks.
        </p>
        <p>
          The aim is simple: information you would get from a pharmacist, written plainly, with nothing overstated. Guidance changes, so pages carry a visible last-reviewed date and are revisited when the underlying guidance is updated.
        </p>

        <h2>Guides reviewed by Usama</h2>
        <p>A selection of the content he has reviewed:</p>
        <ul>
          {reviews.map((r) => (
            <li key={r.to}><Link to={r.to}>{r.label}</Link></li>
          ))}
        </ul>
        <p>
          For how the service itself is regulated and kept safe, see <Link to="/guides/is-contraception-direct-safe/">is Contraception Direct safe and legit?</Link>, and to meet the wider clinical team, see <Link to="/about-us/">about us</Link>.
        </p>
      </div>

      <div className="not-prose mt-10 p-6 sm:p-8 bg-secondary/50 rounded-2xl text-center border border-border/60">
        <h2 className="text-xl font-bold mb-2">Speak to a pharmacist</h2>
        <p className="text-foreground/70 mb-5 text-sm max-w-md mx-auto">
          Book a free NHS pharmacist consultation. If the pill is right for you, it is posted discreetly to your door.
        </p>
        <Button asChild variant="cta" size="pillLg">
          <Link to="/book/">Book your free consultation</Link>
        </Button>
      </div>
    </main>

    <Footer />
  </>
);

export default AuthorUsamaAli;
